import {
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import {
  WarningAmberOutlined,
  ErrorOutlined,
  ErrorOutlineOutlined,
} from "@mui/icons-material";
import { PlanningWarning } from "@providers/hop-ord-server/api";

export interface AutomationEvent {
  updatedAt: string;
  eventDetails: string;
  eventType: string;
}

interface StatusPageDialogProps {
  finishedAt?: string | null;
  warnings: PlanningWarning[];
  error?: string | null;
  open: boolean;
  onClose: () => void;
}

const Event = ({
  index,
  timestamp,
  detail,
}: {
  index?: number;
  timestamp: string;
  detail: string;
}) => (
  <Stack
    key={index}
    direction="row"
    sx={{
      gap: 1,
      alignItems: "flex-start",
    }}
  >
    {typeof index === "number" && (
      <Typography
        variant="body2"
        sx={{ minWidth: "20px" }}
      >{`${index + 1}.`}</Typography>
    )}
    <Typography variant="body2">{`[${dayjs(timestamp).format("YYYY-MM-DD, HH:mm:ss")}] ${detail}`}</Typography>
  </Stack>
);

export const StatusPageDialog = ({
  warnings,
  error,
  finishedAt,
  open,
  onClose,
}: StatusPageDialogProps) => {
  const hasError = error && error.trim().length > 0;
  const hasWarnings = warnings && warnings.length > 0;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      scroll={"paper"}
    >
      <DialogTitle>
        {hasError && hasWarnings
          ? "Errors and warnings"
          : hasError
            ? "Errors"
            : "Warnings"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {hasError && (
            <Stack
              sx={{
                gap: 1,
              }}
            >
              {hasWarnings && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    color: "error.dark",
                  }}
                >
                  <ErrorOutlineOutlined fontSize="small" />
                  <Typography variant="subtitle2">Errors</Typography>
                </Stack>
              )}
              <Stack
                sx={{
                  gap: 1,
                  pl: 0.5,
                }}
              >
                <Event
                  index={0}
                  timestamp={finishedAt || new Date().toISOString()}
                  detail={error}
                />
              </Stack>
            </Stack>
          )}
          {hasWarnings && (
            <Stack
              sx={{
                gap: 1,
              }}
            >
              {hasError && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningAmberOutlined color="warning" fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "warning.dark",
                    }}
                  >
                    Warnings
                  </Typography>
                </Stack>
              )}
              <Stack
                sx={{
                  gap: 1,
                  pl: 0.5,
                }}
              >
                {warnings.map((warning, index) => (
                  <Event
                    key={index}
                    index={index}
                    timestamp={warning.updatedAt}
                    detail={warning.message}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface WarningBoxProps {
  onClick: () => void;
  numberOfWarnings: number;
}

interface WarningBoxContentsProps {
  onClick: () => void;
  numberOfWarnings: number;
  color: "warning" | "error";
}

const WarningBoxContents = ({
  numberOfWarnings,
  onClick,
  color,
}: WarningBoxContentsProps) => (
  <Stack
    direction="row"
    sx={{
      alignItems: "center",
      gap: 0.3,
    }}
  >
    <Typography variant="body2" color={`${color}.dark`}>
      {numberOfWarnings} warning{numberOfWarnings === 1 ? "" : "s"} detected and
      skipped
    </Typography>
    <Button
      onClick={onClick}
      sx={{ textDecoration: "underline", textTransform: "uppercase" }}
      size="small"
      color={color}
      variant="text"
    >
      VIEW DETAILS
    </Button>
  </Stack>
);

export const WarningBox = ({ onClick, numberOfWarnings }: WarningBoxProps) => {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 1,
        marginTop: 2,
        padding: 1,
        backgroundColor: "warning.light",
      }}
    >
      <WarningAmberOutlined color="warning" fontSize="small" />
      <Stack>
        <Typography
          variant="subtitle2"
          sx={{
            color: "warning.dark",
          }}
        >
          Warnings
        </Typography>
        <WarningBoxContents
          numberOfWarnings={numberOfWarnings}
          onClick={onClick}
          color="warning"
        />
      </Stack>
    </Stack>
  );
};

interface ErrorBoxProps {
  error: string;
  errorDate: string;
  numberOfWarnings: number;
  onClick: () => void;
}

export const ErrorBox = ({
  error,
  errorDate,
  numberOfWarnings,
  onClick,
}: ErrorBoxProps) => {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 1,
        marginTop: 2,
        padding: 1,
        backgroundColor: "error.light",
      }}
    >
      <ErrorOutlined color="error" fontSize="small" sx={{ marginTop: 0.5 }} />
      <Stack>
        <Typography
          variant="subtitle1"
          sx={{
            color: "error.dark",
          }}
        >
          Plan failure errors
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "error.dark",
            maxHeight: 150,
            overflow: "auto",
          }}
        >
          {`[${dayjs(errorDate).format("YYYY-MM-DD, HH:mm:ss")}] ${error}`}
        </Typography>
        {numberOfWarnings > 0 && (
          <WarningBoxContents
            numberOfWarnings={numberOfWarnings}
            onClick={onClick}
            color="error"
          />
        )}
      </Stack>
    </Stack>
  );
};
